<script setup>
  import {computed, onBeforeMount, ref, watch} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import ComponentInput from "@components/Inputs/Input.vue";
  import {useForm} from "@inertiajs/vue3";
  import {MedButton, MedLink} from "@pharma4u/medicheck-components";
  import {isEqual} from "es-toolkit/compat";
  import Cookies from "js-cookie";

  defineOptions({
    name: "PagesLoginIndex",
  });

  const props = defineProps({
    title: {
      type: String,
      default: null,
    },
    p4uUrl: {
      type: String,
      default: null,
    },
    cookieDomain: {
      type: String,
      default: null,
    },
    browserWarning: {
      type: Boolean,
      default: false,
    },
    browserSupported: {
      type: Boolean,
      default: true,
    },
  });

  const form = useForm({
    username: "",
    password: "",
  });

  const hasTriedToSubmit = ref(false);
  const isPos = ref(false);
  const loginForm = ref(null);
  const loginUrl = ref("");
  const redirectUrl = ref("");

  onBeforeMount(() => {
    // redirect to get parameter, default to magic onboarding route
    const params = new URLSearchParams(globalThis.location.search);
    redirectUrl.value = params.get("redirect") ?? route("onboarding");
    loginUrl.value = props.p4uUrl + "/allgemein/benutzer/login";
  });

  const isInvalidUsername = computed(() => hasTriedToSubmit.value && form.username.length === 0);
  const isInvalidPassword = computed(() => hasTriedToSubmit.value && form.password.length === 0);
  const isValidForm = computed(() => !isInvalidUsername.value || !isInvalidPassword.value);

  watch(
    () => form.username,
    () => {
      hasTriedToSubmit.value = false;
    },
  );

  watch(
    () => form.password,
    () => {
      hasTriedToSubmit.value = false;

      isEqual(form.password, form.passwordConfirmation);
    },
  );

  function performLoginAction() {
    hasTriedToSubmit.value = true;

    if (isValidForm.value) {
      Cookies.set("fe_typo_user", "56f2bbf46eadb8423eb49df04552c1f9", {
        expires: 1,
        path: "/",
        domain: props.cookieDomain,
      });

      loginForm.value.submit();
    }
  }
</script>

<template>
  <div id="bg-login" class="-mx-4 -mb-4 flex flex-1 grow bg-cover bg-top">
    <div class="grid flex-1 place-items-center bg-linear-to-b from-gray-50 px-5">
      <div class="w-md rounded-md bg-white p-4 shadow-md sm:w-96">
        <template v-if="!isPos">
          <h1 class="text-lg font-semibold">MediCheck &ndash; Login</h1>

          <ComponentAlert v-if="browserWarning || browserSupported === false" type="warning" :test-id="getTestId()">
            Ihr Browser ist veraltet oder wird offiziell nicht unterstützt. Dies kann zu unerwünschten Folgen bei der
            Benutzung dieser Software führen.
          </ComponentAlert>

          <form ref="loginForm" method="post" name="loginForm" :action="loginUrl" :data-test="getTestId('form')">
            <input type="hidden" name="logintype" value="login" :data-test="getTestId('form-login-type-input')" />

            <input type="hidden" name="pid" value="707" :data-test="getTestId('form-pid-input')" />

            <input
              type="hidden"
              name="redirect_url"
              :value="redirectUrl"
              :data-test="getTestId('form-redirect-url-input')"
            />

            <input
              type="hidden"
              name="tx_felogin_pi1[noredirect]"
              value="0"
              :data-test="getTestId('form-tx_felogin_pi1[noredirect]-input')"
            />

            <ComponentInput
              ref="usernameInput"
              v-model="form.username"
              class="mt-2"
              label="E-Mail-Adresse des pharma4u-Accounts"
              name="user"
              :validation="isInvalidUsername ? 'Bitte geben Sie Ihre E-Mail-Adresse des pharma4u-Accounts an.' : null"
              :test-id="getTestId('form-username')"
              @keyup.enter="performLoginAction"
            >
              <template #icon>
                <ComponentIcon class="text-xl text-gray-500">email</ComponentIcon>
              </template>
            </ComponentInput>

            <ComponentInput
              v-model="form.password"
              class="mt-4"
              type="password"
              label="Passwort"
              name="pass"
              :validation="isInvalidPassword ? 'Bitte geben Sie Ihr pharma4u Passwort ein.' : null"
              :test-id="getTestId('form-password')"
              @keyup.enter="performLoginAction"
            >
              <template #icon>
                <ComponentIcon class="text-xl text-gray-500">lock</ComponentIcon>
              </template>
            </ComponentInput>

            <p class="mt-4">
              <MedLink
                variant="primary"
                href="https://www.pharma4u.de/allgemein/benutzer/login/?tx_felogin_pi1%5Bforgot%5D=1"
                :data-test="getTestId('form-password-forgot-link')"
              >
                Passwort vergessen?
              </MedLink>
            </p>

            <MedButton
              class="mt-4 w-full justify-center"
              type="button"
              variant="primary"
              :data-test="getTestId('form-submit-button')"
              @click="performLoginAction"
            >
              Login
            </MedButton>
          </form>

          <p class="mt-6 text-center">
            Noch kein Account vorhanden?

            <br />

            <MedLink
              variant="primary"
              href="https://www.pharma4u.de/apotheker/medicheck/registrieren"
              :data-test="getTestId('register-link')"
            >
              Jetzt registrieren!
            </MedLink>
          </p>
        </template>

        <template v-else>
          <h1 class="text-lg font-semibold">Benutzersitzung abgelaufen!</h1>

          <p class="mt-4">
            Leider ist Ihre Benutzersitzung abgelaufen. Bitte nutzen Sie Ihre WaWi, um diese Analyse wieder zu öffnen.
          </p>
        </template>
      </div>
    </div>
  </div>
</template>

<style>
  #bg-login {
    background-image: url("/resources/assets/images/hero-image.jpg");
  }
</style>
